import type { UserMode } from '@respiria/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/useT';
import { TOURS } from './tour-content';

/**
 * The spotlight itself. Dims the whole app with an SVG mask that punches a
 * rounded hole around the current step's target, and shows a tooltip near it.
 * Re-measures on scroll/resize and on a short interval so it follows a target
 * that mounts after a route change; if the target is absent the step becomes a
 * centered card (see tour-content.ts).
 */
const PAD = 8;
const RADIUS = 14;

type Rect = { top: number; left: number; width: number; height: number };

const sameRect = (a: Rect | null, b: Rect | null) =>
  a === b ||
  (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

export function TourOverlay({ mode, onClose }: { mode: UserMode; onClose: () => void }) {
  const t = useT();
  const steps = TOURS[mode];
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const scrolledForStep = useRef(-1);

  const step = steps[index]!;
  const isLast = index === steps.length - 1;

  const measure = useCallback(() => {
    const target = step.target;
    if (!target) {
      setRect((r) => (r === null ? r : null));
      return;
    }
    const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
    if (!el) {
      setRect((r) => (r === null ? r : null));
      return;
    }
    // Bring it into view once per step, then track its live position.
    if (scrolledForStep.current !== index) {
      scrolledForStep.current = index;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    const r = el.getBoundingClientRect();
    const next = { top: r.top, left: r.left, width: r.width, height: r.height };
    setRect((prev) => (sameRect(prev, next) ? prev : next));
  }, [step.target, index]);

  useEffect(() => {
    measure();
    const id = window.setInterval(measure, 200);
    const onScroll = () => measure();
    const onResize = () => {
      setVp({ w: window.innerWidth, h: window.innerHeight });
      measure();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [measure]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const goNext = () => (isLast ? onClose() : setIndex((i) => i + 1));
  const goPrev = () => setIndex((i) => Math.max(0, i - 1));

  const hole = rect
    ? { x: rect.left - PAD, y: rect.top - PAD, w: rect.width + PAD * 2, h: rect.height + PAD * 2 }
    : null;

  // Prefer placing the tooltip below the target; flip above when there's no room.
  const placeBelow = !rect || vp.h - (rect.top + rect.height) > 240;
  const tipStyle: React.CSSProperties = !rect
    ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : placeBelow
      ? { top: rect.top + rect.height + PAD + 14, left: '50%', transform: 'translateX(-50%)' }
      : { bottom: vp.h - rect.top + PAD + 14, left: '50%', transform: 'translateX(-50%)' };

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={t(step.titleKey)}>
      <svg width={vp.w} height={vp.h} className="absolute inset-0" aria-hidden focusable="false">
        <defs>
          <mask id="tour-hole">
            <rect x={0} y={0} width={vp.w} height={vp.h} fill="white" />
            {hole && <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={RADIUS} fill="black" />}
          </mask>
        </defs>
        <rect x={0} y={0} width={vp.w} height={vp.h} fill="rgba(0,0,0,0.66)" mask="url(#tour-hole)" />
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx={RADIUS}
            fill="none"
            stroke="var(--brand)"
            strokeWidth={2}
          />
        )}
      </svg>

      <div
        className="fixed w-[min(360px,calc(100vw-32px))] rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)]"
        style={tipStyle}
      >
        <p className="mb-1 text-[12px] font-semibold text-brand">
          {index + 1}/{steps.length}
        </p>
        <h3 className="text-[17px] font-bold text-content">{t(step.titleKey)}</h3>
        <p className="mt-1.5 text-[14px] leading-relaxed text-muted">{t(step.bodyKey)}</p>
        <div className="mt-4 flex items-center justify-between">
          <button type="button" onClick={onClose} className="text-[13px] font-medium text-muted">
            {t('tour.skip')}
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                type="button"
                onClick={goPrev}
                className="min-h-9 rounded-[var(--radius-btn)] bg-surface-2 px-3 text-[14px] font-semibold text-content transition-transform active:scale-[0.98]"
              >
                {t('tour.prev')}
              </button>
            )}
            <button
              type="button"
              onClick={goNext}
              className="min-h-9 rounded-[var(--radius-btn)] bg-brand px-4 text-[14px] font-semibold text-white transition-transform active:scale-[0.98]"
            >
              {isLast ? t('tour.done') : t('tour.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
